@if (visible) {
  <div class="sales-modal__overlay">
    <div class="sales-modal" role="dialog" aria-modal="true"
         aria-labelledby="sales-modal-title" aria-describedby="sales-modal-intro"
         cdkTrapFocus [cdkTrapFocusAutoCapture]="true" (keydown.escape)="close()">
      <div class="sales-modal__head">
        <div>
          <div class="sales-modal__sub">{{ 'benchSales.subtitle' | translate }}</div>
          <div class="sales-modal__title" id="sales-modal-title">{{ 'benchSales.title' | translate }}</div>
        </div>
        <button class="sales-modal__x" type="button" [attr.aria-label]="'general.close' | translate" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="sales-modal__body">
        <p class="sales-modal__intro" id="sales-modal-intro">{{ 'benchSales.intro' | translate }}</p>

        @for (firm of firms; track firm) {
          <div class="sales-modal__firm">
            <div class="sales-modal__firm-head">
              <app-company-badge [abbreviation]="firm"></app-company-badge>
              <span class="sales-modal__firm-count">
                {{ 'benchSales.firmCount' | translate: {count: firmCount(firm)} }}
              </span>
              <span class="sales-modal__add-field">
                <input class="sales-modal__input sales-modal__add-input" type="text" #addInput
                       [(ngModel)]="addSearch[firm]" [matAutocomplete]="autoAdd"
                       [placeholder]="'benchSales.addPlaceholder' | translate"
                       [attr.aria-label]="'benchSales.addSales' | translate">
                <mat-autocomplete #autoAdd="matAutocomplete" class="sales-add-autocomplete"
                                  (optionSelected)="onAddSelected(firm, $event.option.value, addInput)">
                  @for (user of filteredUsers(firm); track user.id) {
                    <mat-option [value]="user.id">{{ userLabel(user) }}</mat-option>
                  } @empty {
                    <mat-option [disabled]="true">{{ 'benchSales.noSelectableUsers' | translate }}</mat-option>
                  }
                </mat-autocomplete>
              </span>
            </div>

            <div class="sales-modal__grid-head">
              <span>{{ 'benchSales.col.abbr' | translate }}</span>
              <span>{{ 'benchSales.col.name' | translate }}</span>
              <span>{{ 'benchSales.col.substitute' | translate }}</span>
              <span>{{ 'benchSales.col.absentFrom' | translate }}</span>
              <span>{{ 'benchSales.col.absentUntil' | translate }}</span>
              <span>{{ 'benchSales.col.reason' | translate }}</span>
              <span></span>
            </div>

            @for (s of membersByFirm(firm); track s.id) {
              <div class="sales-modal__row" [class.sales-modal__row--absent]="isAbsent(s)">
                <span class="sales-modal__kuerzel sales-modal__value">{{ s.abbreviation || '—' }}</span>
                <span class="sales-modal__value">{{ s.fullName || '—' }}</span>

                <select class="sales-modal__input" [(ngModel)]="s.substituteUserId" (ngModelChange)="persist(s)">
                  <option [ngValue]="null">{{ 'benchSales.noSubstitute' | translate }}</option>
                  @for (option of substituteOptions(s); track option.id) {
                    <option [ngValue]="option.userId">{{ memberLabel(option) }}</option>
                  }
                </select>

                <div class="sales-modal__date">
                  <input class="sales-modal__date-input" [matDatepicker]="fromPicker" placeholder="—"
                         [ngModel]="toMoment(s.absenceFrom)"
                         (ngModelChange)="s.absenceFrom = toIso($event); persist(s)">
                  <mat-datepicker-toggle class="sales-modal__date-toggle" [for]="fromPicker"></mat-datepicker-toggle>
                  <mat-datepicker #fromPicker></mat-datepicker>
                </div>

                <div class="sales-modal__date" [class.sales-modal__date--invalid]="hasInvalidRange(s)"
                     [title]="hasInvalidRange(s) ? ('benchSales.invalidRange' | translate) : null">
                  <input class="sales-modal__date-input" [matDatepicker]="untilPicker" placeholder="—"
                         [min]="toMoment(s.absenceFrom)"
                         [ngModel]="toMoment(s.absenceUntil)"
                         (ngModelChange)="s.absenceUntil = toIso($event); persist(s)">
                  <mat-datepicker-toggle class="sales-modal__date-toggle" [for]="untilPicker"></mat-datepicker-toggle>
                  <mat-datepicker #untilPicker></mat-datepicker>
                </div>

                <input class="sales-modal__input" type="text"
                       [placeholder]="'benchSales.reasonPlaceholder' | translate"
                       [(ngModel)]="s.absenceReason" (blur)="persist(s)">

                <span class="sales-modal__flag">
                  @if (isSaving(s)) {
                    <span class="sales-modal__flag-ok">{{ 'benchSales.saving' | translate }}</span>
                  } @else if (isAbsent(s)) {
                    <span class="sales-modal__flag-absent">
                      <mat-icon>event_busy</mat-icon>{{ 'benchSales.statusAbsent' | translate }}
                    </span>
                  } @else {
                    <span class="sales-modal__flag-ok">{{ 'benchSales.statusActive' | translate }}</span>
                  }
                  <button class="sales-modal__del" type="button"
                          [title]="'benchSales.removeSales' | translate"
                          [attr.aria-label]="'benchSales.removeSales' | translate" (click)="requestRemove(s)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </span>
              </div>
            } @empty {
              <div class="sales-modal__empty">{{ 'benchSales.emptyForFirm' | translate: {firm: firm} }}</div>
            }
          </div>
        }
      </div>

      <div class="sales-modal__foot">
        <span class="sales-modal__count">
          {{ 'benchSales.summary' | translate: {count: totalCount, represented: representedCount} }}
        </span>
        <div class="sales-modal__actions">
          <button class="sales-modal__btn-primary" type="button" (click)="close()">
            {{ 'general.close' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <app-modal
    class="sales-modal__confirm-modal"
    [visible]="!!pendingRemoval"
    [showClose]="true"
    [title]="'benchSales.deleteConfirm.title'"
    [saveText]="'general.delete'"
    [contentTemplate]="removeConfirmContent"
    (save)="confirmRemove()"
    (cancel)="cancelRemove()"
  ></app-modal>
  <ng-template #removeConfirmContent>
    <p>{{ 'benchSales.deleteConfirm.text' | translate: {name: pendingRemovalLabel} }}</p>
  </ng-template>
}
